import React, { useContext, useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase/browser'
import { logger } from '../lib/logger'
import { SUPABASE_CONFIG } from '../config/supabase'
import { AuthContext } from './AuthContextCore'
import { HotelContext, type Organization } from './HotelContextCore'

// Import AuthUser type from AuthContext (unused but kept for reference)
// type AuthUser = {
//   id: string
//   email?: string
//   profile?: Database['public']['Tables']['profiles']['Row']
//   memberships?: Database['public']['Tables']['memberships']['Row']
// }

export function HotelProvider({ children }: { children: React.ReactNode }) {
  // Safely get auth context - use useContext directly to avoid throwing
  const authContext = useContext(AuthContext)
  const user = authContext?.user ?? null
  const authLoading = authContext?.isLoading ?? true
  const [selectedHotel, setSelectedHotel] = useState<Organization | null>(null)
  const [availableHotels, setAvailableHotels] = useState<Organization[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoadedHotels, setHasLoadedHotels] = useState(false)
  const [selectedHotelIsPremium, setSelectedHotelIsPremium] = useState(false)
  const [selectedHotelPremiumResolved, setSelectedHotelPremiumResolved] = useState(false)
  const hasRestoredHotel = useRef(false)
  const selectedHotelRef = useRef<Organization | null>(null)
  
  // Keep ref in sync with state
  useEffect(() => {
    selectedHotelRef.current = selectedHotel
  }, [selectedHotel])
  
  // Remove the authLoading check that was causing the issue
  // The loading state is now managed by fetchAvailableHotels
  // Fixed useNavigate import issue - COMPLETELY REMOVED

  // @ts-expect-error - Intentionally unused, reserved for future use
  const _loadSelectedHotel = useCallback(async (hotelId: string) => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', hotelId)
        .eq('is_active', true)
        .single()

      if (error) {
        logger.error('Error loading selected hotel', error)
        // Clear invalid hotel from localStorage
        localStorage.removeItem('selectedHotelId')
        setSelectedHotel(null)
        return false
      }

      setSelectedHotel(data)
      return true
    } catch (err) {
      logger.error('Error loading selected hotel', err)
      // Clear invalid hotel from localStorage
      localStorage.removeItem('selectedHotelId')
      setSelectedHotel(null)
      return false
    }
  }, [])

  // Persist selected hotel when it changes
  useEffect(() => {
    if (selectedHotel) {
      localStorage.setItem('selectedHotelId', selectedHotel.id)
    }
  }, [selectedHotel])




  const fetchAvailableHotels = useCallback(async () => {
    try {
      setIsLoading(true)
      
      if (!user?.id) {
        setAvailableHotels([])
        setIsLoading(false)
        return
      }
      
      if (!user?.id || user.id === 'undefined' || user.id === 'null') {
        setAvailableHotels([])
        setIsLoading(false)
        return
      }
      
      // Get user's memberships to find which hotels they have access to
      // Check if Supabase client is properly initialized
      if (!supabase) {
        logger.error('Supabase client not initialized')
        setIsLoading(false)
        return
      }

      const { data: memberships, error: membershipError } = await supabase
        .from('memberships')
        .select(`
          org_id,
          organizations (
            id,
            name,
            address,
            city,
            state,
            zip_code,
            phone,
            email,
            website,
            image_url,
            description,
            is_active,
            created_at,
            updated_at,
            night_audit_report_profile
          )
        `)
        .eq('user_id', user.id)

      if (membershipError) {
        logger.error('Error fetching memberships', membershipError)
        // If it's a network error, log more details
        if (membershipError.message?.includes('Failed to fetch')) {
          logger.error('Network error - check Supabase connection', {
            url: SUPABASE_CONFIG?.url,
            hasAnonKey: !!SUPABASE_CONFIG?.anonKey
          })
        }
        setIsLoading(false)
        return
      }

      // Extract organizations from memberships
      // Handle both single object and array responses from Supabase
      const hotels = memberships?.map((membership: any) => {
        const org = membership.organizations
        // Handle case where organizations might be in an array
        if (Array.isArray(org)) {
          return org[0]
        }
        return org
      })
      // Filter out null/undefined and inactive hotels
      .filter((org: any) => org && org.is_active !== false) || []
      
      setAvailableHotels(hotels)
      
      // If we have hotels but no selected hotel yet, restore or auto-select
      // Only do this if we haven't restored yet (to avoid overriding manual selections)
      // IMPORTANT: Only restore if there's a saved hotel ID (session restore), not on fresh login
      const currentSelectedHotel = selectedHotelRef.current
      if (hotels.length > 0 && !currentSelectedHotel && !hasRestoredHotel.current) {
        const savedHotelId = localStorage.getItem('selectedHotelId')
        if (savedHotelId) {
          // Only restore if saved hotel exists (this is a session restore, not fresh login)
          const savedHotel = hotels.find((h: any) => h?.id === savedHotelId)
          if (savedHotel) {
            setSelectedHotel(savedHotel)
            hasRestoredHotel.current = true
          } else {
            // Saved hotel not found, clear it but don't auto-select (user should choose)
            localStorage.removeItem('selectedHotelId')
          }
        }
        // Don't auto-select on fresh login - let user choose at hotel-selection page
        // Only restore if there was a saved hotel ID (session restore)
      } else if (hotels.length > 0 && currentSelectedHotel) {
        // Verify current selected hotel is still in available hotels
        const stillAvailable = hotels.find((h: any) => h?.id === currentSelectedHotel.id)
        if (!stillAvailable) {
          // Current hotel no longer available, switch to first available
          setSelectedHotel(hotels[0])
          localStorage.setItem('selectedHotelId', hotels[0].id)
        }
      }
    } catch (err) {
      logger.error('Error fetching hotels', err)
    } finally {
      setIsLoading(false)
      setHasLoadedHotels(true)
    }
  }, [user?.id])

  // Refetch hotels when user changes
  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchAvailableHotels()
    } else if (!authLoading && !user?.id) {
      // Clear hotels if no user
      setAvailableHotels([])
      setSelectedHotel(null)
      setHasLoadedHotels(false)
      localStorage.removeItem('selectedHotelId')
      hasRestoredHotel.current = false
    }
  }, [user?.id, authLoading, fetchAvailableHotels])

  // Reset restoration flag and clear hotel when user logs out
  useEffect(() => {
    if (!user?.id && !authLoading) {
      hasRestoredHotel.current = false
      setSelectedHotel(null)
      setAvailableHotels([])
    }
  }, [user?.id, authLoading])

  const selectHotel = useCallback((hotel: Organization) => {
    setSelectedHotel(hotel)
    localStorage.setItem('selectedHotelId', hotel.id)
    // Mark that we've made a manual selection to prevent auto-restoration from overriding
    hasRestoredHotel.current = true
  }, [])

  const refreshHotels = useCallback(async () => {
    await fetchAvailableHotels()
  }, [fetchAvailableHotels])

  // Resolve whether the selected hotel is on a paid plan (has an admin or paid
  // owner). Fast path: if the current user is admin/owner_tier1 of this hotel,
  // it's paid by definition. Otherwise ask the DB (staff can't read others'
  // memberships directly). The last answer per hotel is cached in localStorage
  // so revisits resolve instantly (no spinner / no flash) while we revalidate.
  useEffect(() => {
    let cancelled = false
    const orgId = selectedHotel?.id
    if (!orgId || !user?.id) {
      setSelectedHotelIsPremium(false)
      setSelectedHotelPremiumResolved(true)
      return
    }
    const myRole = user.memberships?.find((m) => m.org_id === orgId)?.role
    if (myRole === 'admin' || myRole === 'owner_tier1') {
      setSelectedHotelIsPremium(true)
      setSelectedHotelPremiumResolved(true)
      return
    }

    const cacheKey = `hotelPremium:${orgId}`
    const cached = localStorage.getItem(cacheKey)
    if (cached !== null) {
      // Instant from cache; revalidate quietly in the background.
      setSelectedHotelIsPremium(cached === 'true')
      setSelectedHotelPremiumResolved(true)
    } else {
      setSelectedHotelPremiumResolved(false)
    }

    supabase
      .rpc('hotel_has_premium_access', { p_org_id: orgId })
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) {
          logger.error('Error resolving hotel premium access', error)
          if (cached === null) setSelectedHotelIsPremium(false)
        } else {
          setSelectedHotelIsPremium(!!data)
          localStorage.setItem(cacheKey, data ? 'true' : 'false')
        }
        setSelectedHotelPremiumResolved(true)
      })
    return () => {
      cancelled = true
    }
  }, [selectedHotel?.id, user?.id, user?.memberships])


  return (
    <HotelContext.Provider value={{
      selectedHotel,
      availableHotels,
      isLoading,
      hasLoadedHotels,
      selectedHotelIsPremium,
      selectedHotelPremiumResolved,
      selectHotel,
      refreshHotels
    }}>
      {children}
    </HotelContext.Provider>
  )
}
